import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import Pagination from 'react-reactstrap-pagination';
import { toast } from 'react-toastify';
import { api2 } from '~/services/api';
import Loader from '~/components/Loader/Loader';
import { format } from 'date-fns';
import { debounce } from 'lodash';
import ModalCreate from './ModalCreate';

const perPage = 20;

export default function UserEvents() {
    const [userEvents, setUserEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(null);
    const [totalItems, setTotalItems] = useState(0);
    const [selectedPage, setSelectedPage] = useState(1);
    const [emailFilter, setEmailFilter] = useState('');
    const [modalDelete, setModalDelete] = useState(false);
    const [modalCreate, setModalCreate] = useState(false);
    const [eventToDelete, setEventToDelete] = useState(null);

    const loadUserEvents = useCallback(async (page) => {
        setLoading(true);

        let URL = `user-events?page=${page}&limit=${perPage}`;

        if (emailFilter) {
            URL += `&user_email=${encodeURIComponent(emailFilter)}`;
        }

        try {
            const { data } = await api2.get(URL);
            setUserEvents(data?.data || []);
            setTotalItems(data?.total || 0);
            setSelectedPage(page);
        } catch (error) {
            toast.error('Erro ao carregar vínculos');
        } finally {
            setLoading(false);
        }
    }, [emailFilter]);

    useEffect(() => {
        loadUserEvents(1);
    }, [loadUserEvents]);

    const emailDelayedQuery = useRef(
        debounce(e => {
            setEmailFilter(e);
        }, 800)
    ).current;

    const handleDelete = (eventId) => {
        setEventToDelete(eventId);
        setModalDelete(true);
    };

    const confirmDelete = async () => {
        if (!eventToDelete) return;

        setDeleteLoading(eventToDelete);

        try {
            await api2.delete(`user-events/${eventToDelete}`);
            toast.success('Inscrição excluída com sucesso');
            setModalDelete(false);
            setEventToDelete(null);
            loadUserEvents(selectedPage);
        } catch (error) {
            toast.error('Erro ao excluir inscrição');
        } finally {
            setDeleteLoading(null);
        }
    };

    return (
        <>
            <Row>
                <Col>
                    <div className="page-title-box">
                        <Row>
                            <Col lg={7}>
                                <h4 className="page-title">Inscrições de Usuários em Provas</h4>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>

            <Row>
                <Col lg={12}>
                    <Card style={{ overflow: 'auto' }}>
                        <CardBody>
                            {loading && <Loader />}

                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                <div className="card-body-header-title">
                                    <p>Total ({totalItems})</p>
                                    <p className="ml-2 btn btn-primary" onClick={() => setModalCreate(true)}>Cadastrar</p>
                                </div>

                                <div className="card-body-header-status mb-3">
                                    <div className="mr-2" style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span>E-mail do usuário</span>
                                        <input
                                            style={{ padding: 2 }}
                                            type="email"
                                            name="searchEmail"
                                            id="searchEmail"
                                            onChange={e => emailDelayedQuery(e.target.value)}
                                            placeholder="Todos"
                                        />
                                    </div>
                                </div>
                            </div>

                            <table className="table table-centered table-hover mb-0" id="user-events-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Usuário</th>
                                        <th>E-mail</th>
                                        <th>Prova</th>
                                        <th>Distância</th>
                                        <th>Criado em</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {userEvents.length > 0 ? (
                                        userEvents.map((event) => (
                                            <tr key={event.id}>
                                                <td className="tabledit-view-mode">
                                                    <span className="tabledit-span">{event.id}</span>
                                                </td>
                                                <td className="tabledit-view-mode">
                                                    <span className="tabledit-span">
                                                        {event.user?.name || `#${event.user_id}`}
                                                    </span>
                                                </td>
                                                <td className="tabledit-view-mode">
                                                    <span className="tabledit-span">
                                                        {event.user?.email || 'n/a'}
                                                    </span>
                                                </td>
                                                <td className="tabledit-view-mode">
                                                    <span className="tabledit-span">
                                                        {event?.race_name || event.race_calendar_name || event.race_calendar_id || 'n/a'}
                                                        {event?.race_date || event.race_calendar_date ? ` - ${format(new Date(event?.race_date || event.race_calendar_date), 'dd/MM/yyyy')}` : ''}
                                                    </span>
                                                </td>
                                                <td className="tabledit-view-mode">
                                                    {event.distance
                                                        ? <span className="badge badge-info">{event.distance}</span>
                                                        : <span className="tabledit-span">n/a</span>
                                                    }
                                                </td>
                                                <td className="tabledit-view-mode">
                                                    <span className="tabledit-span">
                                                        {event.created_at
                                                            ? format(new Date(event.created_at), 'dd/MM/yyyy HH:mm')
                                                            : 'n/a'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => handleDelete(event.id)}
                                                        disabled={deleteLoading === event.id}
                                                    >
                                                        {deleteLoading === event.id ? 'Excluindo...' : 'Excluir'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        !loading && (
                                            <tr>
                                                <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#999' }}>
                                                    Nenhuma inscrição encontrada.
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col xs={12} style={{ overflow: 'auto' }}>
                    <Pagination
                        firstPageText="Início"
                        lastPageText="Fim"
                        previousPageText="Anterior"
                        nextPageText="Próximo"
                        totalItems={totalItems}
                        pageSize={perPage}
                        defaultActivePage={selectedPage}
                        onSelect={loadUserEvents}
                    />
                </Col>
            </Row>

            <ModalCreate
                showModal={modalCreate}
                toggle={() => setModalCreate(prev => !prev)}
                loadUserEvents={loadUserEvents}
            />

            <Modal isOpen={modalDelete} toggle={() => setModalDelete(prev => !prev)}>
                <ModalHeader toggle={() => setModalDelete(prev => !prev)}>Excluir inscrição</ModalHeader>
                <ModalBody>
                    {deleteLoading && <Loader />}
                    Tem certeza que deseja excluir esta inscrição?
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setModalDelete(prev => !prev)}>Não</Button>{' '}
                    <Button color="danger" onClick={confirmDelete}>Sim</Button>
                </ModalFooter>
            </Modal>
        </>
    );
}
